"use client";

import { Loader2, Mic, MicOff, ChevronDown } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import MicrophoneSelector from "./MicrophoneSelector";
import RecordingPanel from "./RecordingPanel";
import { useAssembly } from "@/context/AssemblyStreaming";
import { useMicrophone } from "@/context/MicrophoneAssembly";
import { microphoneErrorMessage, microphoneSupportError } from "@/lib/assembly/microphone";
import { useToast } from "@/components/ui/use-toast";

const MICROPHONE_STORAGE_KEY = "selectedMicrophoneDeviceId";

const VoiceCommentInput = ({
  onTranscriptionComplete,
  onPartialTranscription,
  disabled = false,
  compact = false,
  showDropdownIndicator = false,
  hideMicSelector = false,
  // "overlay" covers the page while recording; "dock" is a bar at the
  // bottom that leaves the page readable (dictating against a guide).
  recordingLayout = "overlay",
  // "outline" is the bordered button; "ghost" is a bare round icon for a
  // row that has no box of its own (Sabine's composer).
  variant = "outline",
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [isSwitchingMic, setIsSwitchingMic] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [unsupportedReason, setUnsupportedReason] = useState("");
  const [selectedMicDeviceId, setSelectedMicDeviceId] = useState(() => {
    try { return globalThis.localStorage?.getItem(MICROPHONE_STORAGE_KEY) || null; }
    catch { return null; }
  });
  const mountedRef = useRef(true);
  const activeRef = useRef(false);
  const unsubscribeRef = useRef(null);
  const stopPromiseRef = useRef(null);
  const onTranscriptionCompleteRef = useRef(onTranscriptionComplete);
  const onPartialTranscriptionRef = useRef(onPartialTranscription);
  onTranscriptionCompleteRef.current = onTranscriptionComplete;
  onPartialTranscriptionRef.current = onPartialTranscription;
  const { toast } = useToast();
  const {
    connect, disconnect, sendAudio, finalizeSession, connecting, connectionReady,
    transcript: accumulatedTranscript, partialTranscript: partialText, fullTranscript, error,
  } = useAssembly();
  const { startMicrophone, stopMicrophone, subscribeAudio, microphoneDeviceName } = useMicrophone();

  useEffect(() => {
    mountedRef.current = true;
    setUnsupportedReason(microphoneSupportError() || "");
    return () => {
      mountedRef.current = false;
      unsubscribeRef.current?.();
      if (activeRef.current) { void stopMicrophone(); void disconnect(); }
    };
  }, [disconnect, stopMicrophone]);

  useEffect(() => {
    if (activeRef.current && error) toast({ title: "Transcription Error", description: error, variant: "destructive" });
  }, [error, toast]);

  useEffect(() => {
    if (isRecording) onPartialTranscriptionRef.current?.(fullTranscript, true);
  }, [fullTranscript, isRecording]);

  const reportError = useCallback(error => {
    if (error.name !== "AbortError") toast({
      title: "Microphone Error", description: microphoneErrorMessage(error), variant: "destructive",
    });
  }, [toast]);

  const startRecording = async () => {
    if (activeRef.current || isStarting) return;
    setIsStarting(true);
    activeRef.current = true;
    try {
      await stopPromiseRef.current;
      await stopMicrophone();
      await disconnect();
      await connect({ mode: "manual" });
      if (!mountedRef.current) return;
      unsubscribeRef.current = subscribeAudio((pcm, chunk) => {
        sendAudio(pcm);
        if (mountedRef.current) setAudioLevel(Math.min(1, (chunk?.micLevel || 0) * 3));
      });
      const result = await startMicrophone(false, selectedMicDeviceId);
      if (result?.deviceId) setSelectedMicDeviceId(result.deviceId);
      if (mountedRef.current) setIsRecording(true);
    } catch (error) {
      activeRef.current = false;
      unsubscribeRef.current?.();
      await stopMicrophone();
      await disconnect();
      reportError(error);
    } finally {
      if (mountedRef.current) setIsStarting(false);
    }
  };

  const stopRecording = useCallback(() => {
    if (stopPromiseRef.current) return stopPromiseRef.current;
    setIsRecording(false);
    setIsStopping(true);
    onPartialTranscriptionRef.current?.("", false);
    stopPromiseRef.current = (async () => {
      try {
        // Capture flushes its last frame while the direct subscriber is attached.
        const result = await finalizeSession(async () => {
          try { await stopMicrophone({ flush: true }); }
          finally { unsubscribeRef.current?.(); }
        });
        if (result.captureError) reportError(result.captureError);
        if (result.pendingAudioMs) toast({
          title: "Recording interrupted", description: "Some audio could not be sent. Review the text before continuing.", variant: "destructive",
        });
        if (mountedRef.current && result.transcript) onTranscriptionCompleteRef.current?.(result.transcript);
      } catch (error) {
        reportError(error);
      } finally {
        activeRef.current = false;
        await disconnect();
        stopPromiseRef.current = null;
        if (mountedRef.current) { setIsStopping(false); setAudioLevel(0); }
      }
    })();
    return stopPromiseRef.current;
  }, [stopMicrophone, finalizeSession, disconnect, toast, reportError]);

  const switchMicrophone = async deviceId => {
    setSelectedMicDeviceId(deviceId);
    if (!isRecording) return;
    setIsSwitchingMic(true);
    try {
      await stopMicrophone({ flush: true });
      const result = await startMicrophone(false, deviceId);
      if (result?.deviceId) setSelectedMicDeviceId(result.deviceId);
    } catch (error) { reportError(error); }
    finally { setIsSwitchingMic(false); }
  };

  useEffect(() => {
    if (!isRecording) return;
    const onKeyDown = event => {
      if (event.key === "Escape") { event.preventDefault(); void stopRecording(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isRecording, stopRecording]);

  const isBrowserSupported = !unsupportedReason;
  const isConnecting = isStarting || isSwitchingMic || isStopping || (!connectionReady && connecting);
  // Persist mic selection to localStorage
  useEffect(() => {
    if (typeof window !== "undefined" && selectedMicDeviceId) {
      localStorage.setItem(MICROPHONE_STORAGE_KEY, selectedMicDeviceId);
    }
  }, [selectedMicDeviceId]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !selectedMicDeviceId ||
      !navigator?.mediaDevices?.enumerateDevices
    ) {
      return;
    }

    let cancelled = false;

    const validateSelectedDevice = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) {
          return;
        }

        const audioInputs = devices.filter((device) => device.kind === "audioinput");
        const hasComparableIds = audioInputs.some((device) => Boolean(device.deviceId));

        if (!hasComparableIds) {
          return;
        }

        const selectedDeviceStillExists = audioInputs.some(
          (device) => device.deviceId === selectedMicDeviceId
        );

        if (!selectedDeviceStillExists) {
          localStorage.removeItem(MICROPHONE_STORAGE_KEY);
          setSelectedMicDeviceId(null);
        }
      } catch (error) {
        console.warn("[VoiceCommentInput] Unable to validate saved microphone:", error);
      }
    };

    validateSelectedDevice();

    return () => {
      cancelled = true;
    };
  }, [selectedMicDeviceId]);

  const renderIcon = () => {
    if (isConnecting) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }

    const micIcon = isRecording ? (
      <MicOff className="w-4 h-4" />
    ) : (
      <Mic className="w-4 h-4" />
    );

    if (showDropdownIndicator) {
      return (
        <div className="flex items-center gap-1">
          {micIcon}
          <ChevronDown className="w-3 h-3" />
        </div>
      );
    }

    return micIcon;
  };

  const getButtonText = () => {
    if (isStopping) return "Finishing...";
    if (isConnecting) return "Connecting...";
    if (isRecording) return "Stop";
    return "Speak instead";
  };

  return (
    <>
      <div className="flex flex-col items-end gap-2">
        <SimpleTooltip
          content={!isBrowserSupported ? unsupportedReason : getButtonText()}
        >
          <Button
            type="button"
            aria-label={getButtonText()}
            data-testid="voice-comment-input-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              isRecording ? stopRecording() : startRecording();
            }}
            variant={variant}
            size={variant === "ghost" ? "icon" : "sm"}
            disabled={disabled || isConnecting || !isBrowserSupported}
            className={cn(
              "flex-shrink-0 transition-all font-medium",
              variant === "ghost"
                ? "h-9 w-9 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                : "border border-slate-300 text-slate-600 hover:border-slate-400 hover:bg-slate-100 hover:text-slate-700",
              isConnecting && "cursor-wait text-gray-500",
            )}
          >
            {showDropdownIndicator ? (
              <span className="inline-flex items-center">{renderIcon()}</span>
            ) : (
              <>
                <span
                  className={`${compact ? "inline-flex" : "inline-flex sm:hidden"}`}
                >
                  {renderIcon()}
                </span>
                <span
                  className={`${compact ? "hidden" : "hidden sm:inline-flex"} items-center gap-2 px-3 h-10`}
                >
                  {renderIcon()}
                  <span>{getButtonText()}</span>
                </span>
              </>
            )}
          </Button>
        </SimpleTooltip>

        {!isRecording && !compact && !showDropdownIndicator && !hideMicSelector && (
          <div className="hidden sm:block">
            <MicrophoneSelector
              onMicrophoneSelect={setSelectedMicDeviceId}
              selectedDeviceId={selectedMicDeviceId}
            />
          </div>
        )}
      </div>

      {isRecording && (
        <RecordingPanel
          layout={recordingLayout}
          stopRecording={stopRecording}
          audioLevel={audioLevel}
          microphoneDeviceName={microphoneDeviceName}
          connectionReady={connectionReady}
          showMicSelector={!showDropdownIndicator}
          switchMicrophone={switchMicrophone}
          selectedMicDeviceId={selectedMicDeviceId}
          isSwitchingMic={isSwitchingMic}
          accumulatedTranscript={accumulatedTranscript}
          partialText={partialText}
        />
      )}
    </>
  );
};

export default VoiceCommentInput;
